import { deepClone, deepMerge } from '../utils';

export enum RandState {
  Start,
  Started,
  Update,
  Stop,
  Stopped,
}
export class AbortError extends Error {
}
/*
* 可以延迟用户的操作， 或者不进行真实调用，封装后返回给用的Promise就不是原始的了
*/
interface Operation {
  type: RandState;
  action: () => Promise<any>;
  args: any;
  _resolve?: Promise<any>;
  _reject?: Promise<any>;
  resolve: (v: any) => void;
  reject: (err: any) => void;
  promise: Promise<any>;
}
function defaultMergeFunc(args1: any[], args2: any[]) {
  const result = deepClone(args1);
  for (let i = 0; i < args2.length; i++) {
    deepMerge(result[i], args2[i]);
  }
  return result;
}
function defaultResolve(this: Operation, val: any) {
  this._resolve = Promise.resolve(val);
}
function defaultReject(this: Operation, err: any) {
  this._reject = Promise.reject(err);
}
export class RandContext {
  started = false;
  ops: Operation[] = [];
  startSame: (args1: any, args2: any) => boolean = () => true;
  mergeUpdate: (args1: any, args2: any) => any[] = defaultMergeFunc;
  static instances = new WeakMap<object, Map<string, RandContext>>();
  static get(instance: any, group: string | null) {
    if (!group) return;
    const groups = RandContext.instances.get(instance);
    return (groups && groups.get(group)) || new RandContext(instance, group);
  }
  static gets(instance: any, group: RegExp) {
    const groups = RandContext.instances.get(instance);
    const result:RandContext[] = [];
    if (groups) {
      groups.forEach((value, key) => {
        if (group.test(key))result.push(value);
      });
    }
    return result;
  }
  static removeByPattern(instance: any, pattern: RegExp) {
    const groups = RandContext.instances.get(instance);
    if (groups) {
      for (const key of groups.keys()) {
        if (pattern.test(key)) {
          groups.delete(key);
        }
      }
    }
  }
  constructor(public instance: any, public group: string) {
    const groups = RandContext.instances.get(instance);
    if (groups) groups.set(group, this);
    else RandContext.instances.set(instance, new Map([[group, this]]));
  }

  action(s: RandState, func: (...args: any) => Promise<any>, args: any) {
    const _resolve = (val: any) => {
      if (s === RandState.Start) this.started = true;
      else if (s === RandState.Stop) this.started = false;
      this.ops.shift();// 当前操作完成
      this.currentOp?.action(); // 执行下一个
      return val;
    };
    const _reject = (err: unknown) => {
      this.ops.shift();
      // 由于Start失败，Start 之后的 Update 跳过
      if (s === RandState.Start && this.currentOp?.type === RandState.Update) {
        this.ops.shift()!.reject(new AbortError('start failed'));
      }
      // 执行下一个
      this.currentOp?.action(); // 执行下一个
      throw err;
    };
    const op: Operation = {
      type: s,
      action: () => func(...op.args).then(_resolve, _reject),
      args,
      resolve: defaultResolve,
      reject: defaultReject
    };
    try {
      switch (this.state) {
        case RandState.Started:
          if (s === RandState.Start) throw new AbortError('already started');
          break;
        case RandState.Stopped:
          if (s === RandState.Update) throw new AbortError('not started');
          break;
        default:
          return this.cacheOp(op);
      }
    } catch (err) {
      return Promise.reject(err);
    }
    this.ops.push(op);
    return op.promise = func(...op.args).then(_resolve, _reject);
  }
  cacheOp(op: Operation) {
    if (this.ops.length === 1) { // 无排队情况
      // 按照生命周期入队
      switch (this.state) {
        case RandState.Start:
        case RandState.Update:
          if (op.type === RandState.Start) {
            throw new AbortError('already start');
          }
          break;
        case RandState.Stop:
          switch (op.type) {
            case RandState.Update:
              throw new AbortError('update not allowed when stopping');
            case RandState.Stop: // stop stop,忽略后面的stop，返回相同的promise
              return this.currentOp.promise;
          }
          break;
        default:
          throw new AbortError('unknown state');
      }
    } else {
      switch (op.type) {
        case RandState.Stop: // 最后是stop，则忽略之前的所有操作
          switch (this.lastOpType) {
            case RandState.Stop:// 合并stop
              return this.lastOp.promise;
              break;
            default:
              const ignoreError = new AbortError('keep stop');
              this.ops.slice(1).forEach(op => op.reject(ignoreError));
              this.ops = this.ops.slice(0, 1);
              if (this.state === RandState.Stop) {
                return this.currentOp.promise; // 如果当前也是stop返回相同的promise
              }
          }
          break;
        case RandState.Update:
          switch (this.lastOpType) {
            case RandState.Update:
              this.lastOp.args = this.mergeUpdate(this.lastOp.args, op.args);
              return this.lastOp.promise;
            case RandState.Stop:
              throw new AbortError('update not allowed after stop');
          }
          break;
        case RandState.Start:
          switch (this.lastOpType) {
            case RandState.Update: // update start,不符合生命周期
              throw new AbortError('start not allowed after update');
            case RandState.Start:// start start,重复start
              throw new AbortError('duplicate start');
            case RandState.Stop:// start stop start,如果是相同的start最后两次操作可以取消
              if (this.startSame(this.currentOp.args, op.args)) {
                // 去掉最后一个Stop
                this.ops.pop()!.reject!(new AbortError('keep start'));
                throw new AbortError('already start');
              }
          }
      }
    }
    op.promise = new Promise((resolve, reject) => {
      if (op._resolve) op._resolve.then(resolve);// 已经resolve了
      else op.resolve = resolve;
      if (op._reject) op._reject.catch(reject);// 已经reject了
      else op.reject = reject;
    });
    const { action } = op;
    op.action = () => action().then(op.resolve, op.reject);
    this.ops.push(op);
    return op.promise;
  }
  get lastOp() {
    return this.ops[this.ops.length - 1];
  }
  get lastOpType() {
    return this.lastOp.type;
  }
  get currentOp() {
    return this.ops[0];
  }
  get state(): RandState {
    return this.currentOp ? this.currentOp.type : this.started ? RandState.Started : RandState.Stopped;
  }
}
